
import { createAsyncActionCreator, IAsyncActionCreator } from '../store/actions/createActionCreator';
import { mapObject, assign } from '../utilities/objects';
import { subtract, isSubset } from '../utilities/Graft';
import { Signal } from '../utilities/async/Signal';
import { IFaceted, pack } from '../models/Facet';
import {
  IQueuedOperation,
  IOperationPayload,
  startOperation,
  ItemOperationError,
  noRegisteredHandlerErrorFacet
} from '../store/actions/implementOperation';
import { IItemCacheThunkAction, IItemCacheDispatch } from '../store/actions/ItemCacheActions';
import { updateItems } from '../store/reducers/itemMapReducer';
import { CustomError, ICustomErrorProps } from '@ms/utilities-error/lib/index';
import { resolvedFacet, DemandResolution, errorFacet } from '../facets/DemandFacets';
import { readItems } from './readItems';
import { IItemCache } from '../models/IItemCacheObserver';
import { asOnceOnly } from '../store/middleware/RunOnceMiddleware';
import { waitForAll } from '../utilities/async/all';
import { getItemsOperation } from './getItemsOperation';
import { handleError } from './handleError';
import { resolveItems, resolveItemsForBatch, supportFacets, ISupportFacetsPayload } from './resolveItems';
import { excludeResolvedDemands } from '../models/itemCache/excludeResolvedDemands';
import { getItemCacheEvaluator } from '../models/itemCache/ItemCacheEvaluator';
import { ItemResolver, IResolveItemsPayload } from '../models/items/ItemResolver';

export { getItemsOperation };

/**
 * @public
 */
export interface IGetItemsErrorProps extends ICustomErrorProps {
  facets?: IFaceted;
}

/**
 * @public
 */
export class GetItemsError extends CustomError {
  public readonly facets: IFaceted;

  constructor(props: IGetItemsErrorProps) {
    super(props);

    const {
      facets = pack({}, {})
    } = props;

    this.facets = facets;
  }
}

function isUnhandledOperationError(error: Error): boolean {
  return error instanceof ItemOperationError && !!noRegisteredHandlerErrorFacet.unpack(error.facets);
}

/**
 * Implementation for `enqueueOperations` which only appends each incoming
 * operation to the queue if its demands are not met by any existing
 * operations, and then only emits an operation with the remaining demands.
 */
function enqueueGetItemsOperations<TKey extends object>(
  currentOperations: IQueuedOperation<TKey, typeof getItemsOperation.type>[],
  newOperations: IQueuedOperation<TKey, typeof getItemsOperation.type>[]
): IQueuedOperation<TKey, typeof getItemsOperation.type>[] {
  let newCurrentOperations: (typeof currentOperations) | undefined = undefined;

  for (const {
    itemKey,
    startSignal,
    completeSignal,
    options: newOptions
  } of newOperations) {
    let remainingOptions = newOptions;

    let matchingOperationCount = 0;

    const startedWork: Promise<void>[] = [];
    const completedWork: Promise<void>[] = [];

    for (const {
      itemKey: currentItemKey,
      startSignal: currentStartSignal,
      completeSignal: currentCompleteSignal,
      options: currentOptions
    } of currentOperations) {
      if (itemKey === currentItemKey) {
        matchingOperationCount++;
        const previousRemainingOptions = remainingOptions;

        // Remove anything in the new operation that an existing operation already covers.
        remainingOptions = subtract(remainingOptions, currentOptions);

        if (!isSubset(remainingOptions, previousRemainingOptions)) {
          completedWork.push(currentCompleteSignal.promise);
          startedWork.push(currentStartSignal.promise);
        }
      }
    }

    const remainingDemands = remainingOptions.demands;

    if (remainingDemands && Object.keys(remainingDemands).length > 0 || matchingOperationCount === 0) {
      // The new operation has some options which are not met by an existing operation.

      if (!newCurrentOperations) {
        newCurrentOperations = [...currentOperations];
      }

      // TODO Insert a new operation per demanded facet, not an aggregate of all of them.

      const newStartSignal = new Signal<void>();
      const newCompleteSignal = new Signal<void>();
      newCompleteSignal.promise.then(newStartSignal.resolve, newStartSignal.reject);

      startedWork.push(newStartSignal.promise);
      completedWork.push(newCompleteSignal.promise);

      newCurrentOperations.push({
        itemKey: itemKey,
        startSignal: newStartSignal,
        completeSignal: newCompleteSignal,
        options: remainingOptions
      });
    }

    // When any of the work is started, consider the entire request as started.
    Promise.race(startedWork).then(() => startSignal.resolve(), startSignal.reject);

    // When all of the work has completed, consider the entire request as completed.
    waitForAll(completedWork).then(() => completeSignal.resolve(), completeSignal.reject);
  }

  return newCurrentOperations || currentOperations;
}

function resolveRemainingDemands(payload: IOperationPayload<typeof getItemsOperation.type> & {
  resolution: DemandResolution;
}): IItemCacheThunkAction<void> {
  const {
    items,
    resolution
  } = payload;

  return (dispatch: IItemCacheDispatch) => {
    dispatch(batchedUpdateItems({
      items: dispatch(readItems((itemCache: IItemCache) => {
        const unresolvedItems: (typeof updateItems.payloadType)['items'] = {};

        for (const itemKey in items) {
          if (Object.prototype.hasOwnProperty.call(items, itemKey)) {
            const {
              demands
            } = items[itemKey];

            const resolved = itemCache.demandItemFacet(resolvedFacet, itemKey);

            const resolvedDemands = resolved && resolved.demands;

            const newResolutions = mapObject(demands, () => resolution, (facetId: keyof typeof demands) => {
              return !resolvedDemands || !resolvedDemands[facetId] || resolvedDemands[facetId] === DemandResolution.unhandled ?
                facetId :
                undefined;
            });

            unresolvedItems[itemKey] = {
              ...resolvedFacet.pack({
                demands: {
                  ...pack({}, {}),
                  ...newResolutions
                }
              })
            };
          }
        }

        return unresolvedItems;
      }))
    }));
  };
}

export { ISupportFacetsPayload };

export { supportFacets };

export { resolveItemsForBatch };

export { resolveItems };

/**
 * Singleton utility which maintains a current `ItemResolver` which will have its
 * payloads dispatched into the store on a timeout.
 * In order to avoid 'thrashing' the store reducers, store operations which do not require
 * immediate transactional integrity should resolve their items via this utility so their
 * resolutions may be 'batched' even if issued asynchronously.
 */
const getResolveItemsBatcher = asOnceOnly((dispatch: IItemCacheDispatch) => {
  let nextTrain: ItemResolver = new ItemResolver();

  let scheduled: number | undefined;

  function depart(): void {
    const train = nextTrain;

    nextTrain = new ItemResolver();
    scheduled = undefined;

    dispatch(train.execute());
  }

  return {
    resolveItems: (payload: IResolveItemsPayload) => {
      nextTrain.resolveItems(payload);

      if (!scheduled) {
        scheduled = setTimeout(depart, 0);
      }
    },
    updateItems: (payload: typeof updateItems.payloadType) => {
      nextTrain.updateItems(payload);

      if (!scheduled) {
        scheduled = setTimeout(depart, 0);
      }
    },
    flush: () => {
      if (scheduled) {
        clearTimeout(scheduled);
        depart();
      }
    }
  };
});

/**
 * Creator for actions which may be dispatched to get items and their specified facets
 * from their backing data sources, only if the demands are not met by the current state
 * of the item cache.
 * @public
 */
export const getItems: IAsyncActionCreator<IOperationPayload<typeof getItemsOperation.type>, IItemCacheThunkAction<Promise<void>>> =
  createAsyncActionCreator((
    payload: IOperationPayload<typeof getItemsOperation.type>
  ): IItemCacheThunkAction<Promise<void>> => {
    return async (dispatch: IItemCacheDispatch) => {
      // Before checking whether new items need to be fetched, ensure that all pending batched resolutions
      // are flushed to ensure transactional integrity.
      dispatch(getResolveItemsBatcher).flush();

      const {
        items
      } = dispatch(readItems((itemCache: IItemCache) => excludeResolvedDemands(itemCache, payload)));

      if (Object.keys(items).length > 0) {
        const {
          started,
          completed,
          items: itemResults
        } = dispatch(startOperation<typeof getItemsOperation.type>({
          operationTypeFacet: getItemsOperation,
          enqueueOperations: enqueueGetItemsOperations,
          items: items
        }));

        for (const itemKey in itemResults) {
          if (Object.prototype.hasOwnProperty.call(itemResults, itemKey)) {
            // Track individual item requests to handle errors
            // and defend against repeat requests for the same data.
            dispatch(trackItemRequest({
              itemKey,
              options: items[itemKey],
              ...itemResults[itemKey]
            }));
          }
        }

        try {
          await started;
          await completed;
        } catch (e) {
          if (isUnhandledOperationError(e)) {
            dispatch(handleError({
              error: e
            }));

            return;
          }

          throw e;
        }
      }
    };
  });

/**
 * Monitors a single item's request and its resolution state once the backing operation handler
 * has started and completed.
 */
function trackItemRequest(payload: {
  /**
   * The key for the item being requested.
   */
  itemKey: string;
  /**
   * The options provided for the item request.
   */
  options: typeof getItemsOperation.type;
  /**
   * A promise indicating when the operation handler has started processing the item.
   * This will be resolved when the operation handler picks the operation from the queue,
   * or if the operation has been made redundant by early resolution of the data.
   * This will be rejected if the operation is removed from the queue before a handler
   * has started processing it.
   */
  started: Promise<void>;
  /**
   * A promise indicating when the operation handler has completed processing the item.
   */
  completed: Promise<void>;
}): IItemCacheThunkAction<Promise<void>> {
  return async (dispatch: IItemCacheDispatch) => {
    try {
      await payload.started;
      await payload.completed;

      dispatch(resolveRemainingDemands({
        items: {
          [payload.itemKey]: payload.options
        },
        resolution: DemandResolution.unsupported
      }));
    } catch (e) {
      const isUnhandled = isUnhandledOperationError(e);

      if (!isUnhandled) {
        dispatch(batchedResolveItems({
          items: {
            [payload.itemKey]: {
              ...errorFacet.pack(e)
            }
          }
        }));
      }

      dispatch(resolveRemainingDemands({
        items: {
          [payload.itemKey]: payload.options
        },
        resolution: isUnhandled ? DemandResolution.unhandled : DemandResolution.failed
      }));
    }
  };
}

const getGetItemsBatcher = asOnceOnly((dispatch: IItemCacheDispatch) => {
  const itemCacheEvaluator = dispatch(getItemCacheEvaluator);

  let nextTrain: IOperationPayload<typeof getItemsOperation.type> = { items: {} };

  let scheduled: number | undefined;

  function depart(): void {
    const train = nextTrain;

    nextTrain = { items: {} };
    scheduled = undefined;

    itemCacheEvaluator.publishComputedFacets();

    dispatch(getItems(train)).catch(() => {
      // Ignore error.
    });
  }

  return (payload: IOperationPayload<typeof getItemsOperation.type>) => {
    for (const itemKey in payload.items) {
      if (Object.prototype.hasOwnProperty.call(payload.items, itemKey)) {
        const options = payload.items[itemKey];

        const item = nextTrain.items[itemKey] || (nextTrain.items[itemKey] = { demands: pack({}, {}) });

        assign(item.demands, options.demands);
      }
    }

    if (!scheduled) {
      scheduled = setTimeout(depart, 0);
    }
  };
});

/**
 * @internal
 */
export function batchedGetItems(payload: IOperationPayload<typeof getItemsOperation.type>): IItemCacheThunkAction<void> {
  return (dispatch: IItemCacheDispatch) => {
    const getItemsBatcher = dispatch(getGetItemsBatcher);

    getItemsBatcher(payload);
  };
}

/**
 * @internal
 */
function batchedResolveItems(payload: IResolveItemsPayload): IItemCacheThunkAction<void> {
  return (dispatch: IItemCacheDispatch) => {
    const resolveItemsBatcher = dispatch(getResolveItemsBatcher);

    resolveItemsBatcher.resolveItems(payload);
  };
}

/**
 * @internal
 */
function batchedUpdateItems(payload: typeof updateItems.payloadType): IItemCacheThunkAction<void> {
  return (dispatch: IItemCacheDispatch) => {
    const resolveItemsBatcher = dispatch(getResolveItemsBatcher);

    resolveItemsBatcher.updateItems(payload);
  };
}
